import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowLeft, ArrowRight, BadgeCheck, ChartNoAxesCombined,
  LayoutDashboard, Megaphone, Plus, Save, ShieldCheck, Tags, Users, X,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { api } from "../lib/api";

type ServiceRole = "DIRECTOR" | "MANAGER" | "MARKETING" | "SALES" | "FINANCE" | "SUPPORT";
type Section = "overview" | "leads" | "offers" | "campaigns" | "members";
type Service = {
  code: string;
  name: string;
  category: string;
  tagline: string;
  description: string;
  public_url: string;
  published: boolean;
  active: boolean;
  my_role: ServiceRole;
};
type Metrics = { leads: number | null; offers_published: number; campaigns: number | null; members: number };
type Workspace = { service: Omit<Service, "my_role">; my_role: ServiceRole; metrics: Metrics };
type Offer = {
  public_id: string; code: string; version: number; title: string;
  description: string; billing_model: string; price_xof: number | null;
  status: string; approved_at: string | null; can_approve: boolean;
};
type Lead = {
  public_id: string; name: string; company: string; email: string; phone: string;
  need: string; source: string; status: string; created_at: string;
};
type Campaign = {
  public_id: string; title: string; channel: string; budget_xof: number; status: string;
};
type Member = { id: number; display_name: string; username: string; role: ServiceRole; active: boolean };

const sections: { key: Section; label: string; icon: typeof LayoutDashboard }[] = [
  { key: "overview", label: "Vue générale", icon: LayoutDashboard },
  { key: "leads", label: "Prospects", icon: Users },
  { key: "offers", label: "Offres & tarifs", icon: Tags },
  { key: "campaigns", label: "Marketing", icon: Megaphone },
  { key: "members", label: "Équipe", icon: ShieldCheck },
];
const roles: Record<ServiceRole, string> = {
  DIRECTOR: "Direction", MANAGER: "Responsable commercial", MARKETING: "Marketing",
  SALES: "Commercial", FINANCE: "Finance", SUPPORT: "Support",
};
const statuses: Record<string, string> = {
  NEW: "Nouveau", CONTACTED: "Contacté", QUALIFIED: "Qualifié",
  WON: "Gagné", LOST: "Perdu", DRAFT: "Brouillon",
  PUBLISHED: "Publié", RETIRED: "Retiré", ACTIVE: "Active",
  PAUSED: "En pause", CLOSED: "Terminée",
};
function money(value: number | null) {
  return value == null ? "Sur devis" : new Intl.NumberFormat("fr-FR").format(value) + " FCFA";
}
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return <div role="presentation" className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <section role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-2xl">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-black">{title}</h2>
        <button className="rounded-lg p-2 hover:bg-slate-100" onClick={onClose} aria-label="Fermer"><X size={18}/></button>
      </div>
      {children}
    </section>
  </div>;
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block"><span className="label">{label}</span>{children}</label>;
}
function ErrorMessage({ value }: { value: string }) {
  return value ? <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{value}</p> : null;
}

export function CommercialServicesPage() {
  const { code, section } = useParams<{ code: string; section: Section }>();
  const activeSection: Section = sections.some(s => s.key === section) ? section! : "overview";
  const [services, setServices] = useState<Service[]>([]);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [rows, setRows] = useState<(Offer | Lead | Campaign | Member)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState<"lead" | "offer" | "campaign" | "member" | null>(null);
  const [saving, setSaving] = useState(false);
  const requestId = useRef(0);
  const scope = `${code || ""}/${activeSection}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;

  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    setWorkspace(null);
    setRows([]);
    try {
      const catalog = await api.get<Service[]>("/api/v1/services/");
      if (id !== requestId.current) return;
      setServices(catalog);
      if (!code) {
        setWorkspace(null);
        setRows([]);
        return;
      }
      const data = await api.get<Workspace>(`/api/v1/services/${encodeURIComponent(code)}/`);
      if (id !== requestId.current) return;
      setWorkspace(data);
      const endpoint: Record<Exclude<Section, "overview">, string> = {
        leads: "leads", offers: "offers", campaigns: "campaigns", members: "members",
      };
      if (activeSection === "overview") setRows([]);
      else {
        const result = await api.get<(Offer | Lead | Campaign | Member)[]>(
          `/api/v1/services/${encodeURIComponent(code)}/${endpoint[activeSection]}/`
        );
        if (id !== requestId.current) return;
        setRows(result);
      }
    } catch (cause) {
      if (id !== requestId.current) return;
      setError(cause instanceof Error ? cause.message : "Impossible de charger les données commerciales.");
      setWorkspace(null);
      setRows([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [code, activeSection]);

  useEffect(() => {
    setForm(null);
    setNotice("");
    setSaving(false);
    void refresh();
    return () => { requestId.current += 1; };
  }, [refresh]);

  const role = workspace?.my_role;
  const canWriteOffers = role === "DIRECTOR" || role === "MANAGER" || role === "FINANCE";
  const canApprove = role === "DIRECTOR" || role === "FINANCE";
  const canWriteLeads = role === "DIRECTOR" || role === "MANAGER" || role === "SALES";
  const canWriteCampaigns = role === "DIRECTOR" || role === "MANAGER" || role === "MARKETING";
  const canManageCampaigns = role === "DIRECTOR" || role === "MANAGER";
  const canAddMember = role === "DIRECTOR";
  const visibleSections = sections.filter(s =>
    s.key === "leads" ? ["DIRECTOR", "MANAGER", "SALES", "MARKETING"].includes(role || "") :
    s.key === "campaigns" ? ["DIRECTOR", "MANAGER", "MARKETING", "FINANCE"].includes(role || "") : true
  );

  async function mutate(path: string, body?: unknown, method: "POST" | "PATCH" = "POST") {
    const mutationScope = currentScope.current;
    setSaving(true);
    setError("");
    try {
      if (method === "PATCH") await api.patch(path, body);
      else await api.post(path, body || {});
      if (mutationScope !== currentScope.current) return;
      setNotice("Opération enregistrée.");
      await refresh();
      if (mutationScope !== currentScope.current) return;
      setForm(null);
    } catch (cause) {
      if (mutationScope !== currentScope.current) return;
      setError(cause instanceof Error ? cause.message : "Opération refusée.");
    } finally {
      if (mutationScope === currentScope.current) setSaving(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!code || !form) return;
    const data = new FormData(event.currentTarget);
    const get = (key: string) => String(data.get(key) || "").trim();
    const base = `/api/v1/services/${encodeURIComponent(code)}`;
    if (form === "lead") {
      await mutate(base + "/leads/", {
        name: get("name"), company: get("company"), phone: get("phone"),
        email: get("email"), need: get("need"), source: "manual",
      });
    } else if (form === "offer") {
      await mutate(base + "/offers/", {
        code: get("code"), title: get("title"), version: Number(get("version")),
        billing_model: get("billing_model"), description: get("description"),
        price_xof: get("billing_model") === "QUOTE" || get("price_xof") === "" ? null : Number(get("price_xof")),
      });
    } else if (form === "campaign") {
      await mutate(base + "/campaigns/", {
        title: get("title"), channel: get("channel"), budget_xof: Number(get("budget_xof")),
      });
    } else if (form === "member") {
      await mutate(base + "/members/", { email: get("email"), role: get("role") });
    }
  }

  function openForm(type: typeof form) {
    setNotice("");
    setError("");
    setForm(type);
  }

  return <div className="min-h-screen bg-[#f7f8fb] text-slate-950">
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-8">
        <Link to="/services-commerciaux" className="flex items-center gap-3 text-lg font-black">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#fbe4cc] text-[#b94f12]"><ChartNoAxesCombined size={21}/></span>
          SahelTech <span className="font-medium text-slate-500">· Commerce des services</span>
        </Link>
        <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-950">Espace de gestion <ArrowRight className="inline" size={16}/></Link>
      </div>
    </header>
    <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-8 lg:grid-cols-[230px_1fr]">
      <aside className="space-y-3">
        <h2 className="text-xs font-black uppercase tracking-[.14em] text-slate-500">Mes services</h2>
        {services.map(service => <Link key={service.code} to={`/services-commerciaux/${service.code}`}
          className={"block rounded-xl border p-3 text-sm transition " + (service.code === code ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-white hover:border-orange-200")}>
          <span className="block font-bold">{service.name}</span>
          <span className="mt-1 block text-xs text-slate-500">{roles[service.my_role]}</span>
        </Link>)}
        <div className="border-t border-slate-200 pt-4">
          <Link to="/services-commerciaux" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600"><ArrowLeft size={16}/> Tous mes services</Link>
        </div>
      </aside>
      <main className="min-w-0">
        {notice && <p role="status" className="mb-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</p>}
        <ErrorMessage value={error}/>
        {loading ? <p className="py-14 text-sm text-slate-600">Chargement de votre espace…</p> :
          !code ? <section className="space-y-6">
            <div><p className="text-sm font-bold text-orange-700">Portefeuille de services</p>
              <h1 className="mt-1 text-3xl font-black">Espaces commerciaux</h1>
              <p className="mt-2 text-slate-600">Chaque service possède ses prospects, campagnes, offres et équipe, sans partager ses données commerciales.</p>
            </div>
            {services.length ? <div className="grid gap-4 md:grid-cols-2">
              {services.map(item => <Link key={item.code} to={`/services-commerciaux/${item.code}`} className="panel block p-6 hover:border-orange-300">
                <span className="text-xs font-bold uppercase tracking-wider text-orange-700">{item.category || "Service métier"}</span>
                <h2 className="mt-2 text-xl font-black">{item.name}</h2>
                <p className="mt-2 text-sm text-slate-600">{item.tagline || "Espace de gestion commerciale."}</p>
                <p className="mt-5 flex items-center gap-2 text-sm font-semibold">Ouvrir l'espace <ArrowRight size={16}/></p>
              </Link>)}
            </div> : <p className="panel p-6 text-sm text-slate-600">Aucun service ne vous est encore attribué. Contactez la direction SahelTech pour être rattaché à une équipe.</p>}
          </section> : !workspace ? <p className="mt-4 text-sm text-slate-500">Cet espace n'est pas accessible à votre compte.</p> :
          <section>
            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-[.14em] text-orange-700">{workspace.service.category || "Service SahelTech"}</p>
              <h1 className="mt-1 text-3xl font-black">{workspace.service.name}</h1>
              <p className="mt-2 text-sm text-slate-600">{workspace.service.tagline || "Pilotage commercial de ce service."} · {roles[role!]}</p>
            </div>
            <nav aria-label="Navigation commerciale du service" className="mb-6 flex flex-wrap gap-2 border-b border-slate-200 pb-4">
              {visibleSections.map(s => <Link key={s.key} to={`/services-commerciaux/${code}/${s.key}`}
                aria-current={s.key === activeSection ? "page" : undefined}
                className={"inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition " + (activeSection === s.key ? "bg-slate-950 text-white" : "bg-white text-slate-600 hover:bg-slate-100")}>
                <s.icon size={16}/>{s.label}</Link>)}
            </nav>
            {activeSection === "overview" && <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  ["Prospects", workspace.metrics.leads],
                  ["Offres publiées", workspace.metrics.offers_published],
                  ["Campagnes", workspace.metrics.campaigns],
                  ["Membres actifs", workspace.metrics.members],
                ].map(([label, value]) => <div key={String(label)} className="panel p-5">
                  <p className="text-sm text-slate-500">{label}</p><p className="mt-3 text-3xl font-black">{value ?? "—"}</p>
                </div>)}
              </div>
              <div className="panel p-6">
                <h2 className="text-xl font-black">Pilotage commercial indépendant</h2>
                <p className="mt-2 text-sm text-slate-600">Vos offres et vos prospects sont attachés à ce service, pas à l'entreprise du client actuellement sélectionnée.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {visibleSections.filter(s => s.key !== "overview").map(s => <Link key={s.key} to={`/services-commerciaux/${code}/${s.key}`} className="btn-secondary">{s.label} <ArrowRight size={15}/></Link>)}
                </div>
              </div>
            </div>}
            {activeSection === "offers" && <div className="panel overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b p-5"><h2 className="text-lg font-black">Catalogue de tarifs</h2>
                {canWriteOffers && <button className="btn-primary" onClick={() => openForm("offer")}><Plus size={16}/> Nouvelle version</button>}</div>
              <div className="divide-y">
                {(rows as Offer[]).map(o => <div key={o.public_id} className="flex flex-wrap items-center justify-between gap-3 p-5">
                  <div><p className="font-bold">{o.title} <span className="font-normal text-slate-500">v{o.version}</span></p>
                    <p className="mt-1 text-xs text-slate-500">{o.code} · {o.billing_model} · {statuses[o.status]}</p></div>
                  <div className="flex items-center gap-4"><strong>{money(o.price_xof)}</strong>
                    {canApprove && o.can_approve && o.status === "DRAFT" && <button className="btn-secondary" disabled={saving} onClick={() => void mutate(`/api/v1/services/${code}/offers/${o.public_id}/publish/`)}><BadgeCheck size={16}/> Approuver</button>}
                  </div>
                </div>)}
                {!rows.length && <p className="p-5 text-sm text-slate-500">Aucune offre disponible pour ce service.</p>}
              </div>
              <p className="border-t bg-orange-50 p-4 text-xs text-orange-950">Un tarif publié ne peut pas être modifié. Nouvelle version et approbation par une deuxième personne requises.</p>
            </div>}
            {activeSection === "leads" && <div className="panel overflow-hidden">
              <div className="flex items-center justify-between border-b p-5"><h2 className="text-lg font-black">Prospects & CRM</h2>
                {canWriteLeads && <button className="btn-primary" onClick={() => openForm("lead")}><Plus size={16}/> Prospect</button>}</div>
              <div className="divide-y">{(rows as Lead[]).map(lead => <div key={lead.public_id} className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div><p className="font-bold">{lead.name}</p><p className="mt-1 text-sm text-slate-500">{lead.company || lead.phone || lead.email || "Sans coordonnées supplémentaires"} · {lead.source}</p></div>
                {canWriteLeads ? <select aria-label={`Statut de ${lead.name}`} className="field max-w-40" value={lead.status} disabled={saving} onChange={e => void mutate(`/api/v1/services/${code}/leads/${lead.public_id}/`, { status: e.target.value }, "PATCH")}>
                  {["NEW", "CONTACTED", "QUALIFIED", "WON", "LOST"].map(s => <option key={s} value={s}>{statuses[s]}</option>)}
                </select> : <span className="badge bg-slate-100 text-slate-700">{statuses[lead.status]}</span>}
              </div>)}{!rows.length && <p className="p-5 text-sm text-slate-500">Aucun prospect enregistré.</p>}</div>
            </div>}
            {activeSection === "campaigns" && <div className="panel overflow-hidden">
              <div className="flex items-center justify-between border-b p-5"><h2 className="text-lg font-black">Campagnes marketing</h2>
                {canWriteCampaigns && <button className="btn-primary" onClick={() => openForm("campaign")}><Plus size={16}/> Campagne</button>}</div>
              <div className="divide-y">{(rows as Campaign[]).map(c => <div key={c.public_id} className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div><p className="font-bold">{c.title}</p><p className="mt-1 text-xs text-slate-500">{c.channel} · {statuses[c.status]} · Budget : {money(c.budget_xof)}</p></div>
                {canManageCampaigns && c.status === "DRAFT" && <button className="btn-secondary" disabled={saving} onClick={() => void mutate(`/api/v1/services/${code}/campaigns/${c.public_id}/`, { status: "ACTIVE" }, "PATCH")}><Save size={15}/> Activer</button>}
              </div>)}{!rows.length && <p className="p-5 text-sm text-slate-500">Aucune campagne enregistrée.</p>}</div>
              <p className="border-t bg-slate-50 p-4 text-xs text-slate-600">La préparation n'active aucune publicité externe. Les diffusions restent à connecter séparément.</p>
            </div>}
            {activeSection === "members" && <div className="panel overflow-hidden">
              <div className="flex items-center justify-between border-b p-5"><h2 className="text-lg font-black">Équipe du service</h2>
                {canAddMember && <button className="btn-primary" onClick={() => openForm("member")}><Plus size={16}/> Affecter un compte</button>}</div>
              <div className="divide-y">{(rows as Member[]).map(m => <div key={m.id} className="flex items-center justify-between p-5">
                <div><p className="font-bold">{m.display_name}</p><p className="text-xs text-slate-500">{m.username}</p></div><span className="badge bg-slate-100 text-slate-700">{roles[m.role]}</span>
              </div>)}{!rows.length && <p className="p-5 text-sm text-slate-500">Aucun membre actif.</p>}</div>
            </div>}
          </section>
        }
      </main>
    </div>
    {form && <Modal title={({ lead: "Nouveau prospect", offer: "Préparer un tarif", campaign: "Créer une campagne", member: "Affecter un membre" })[form]} onClose={() => setForm(null)}>
      <form className="space-y-4" onSubmit={e => void submit(e)}>
        {form === "lead" && <>
          <Field label="Nom du contact"><input className="field" name="name" required maxLength={160}/></Field>
          <Field label="Entreprise"><input className="field" name="company" maxLength={200}/></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Téléphone"><input className="field" name="phone" maxLength={64}/></Field>
            <Field label="Email"><input className="field" name="email" type="email"/></Field>
          </div>
          <Field label="Besoin"><textarea className="field" name="need" rows={3}/></Field>
        </>}
        {form === "offer" && <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Code de l'offre"><input className="field" name="code" required pattern="[a-z0-9-]+" placeholder="start"/></Field>
            <Field label="Version"><input className="field" name="version" type="number" min={1} defaultValue={1} required/></Field>
          </div>
          <Field label="Nom public"><input className="field" name="title" required maxLength={160}/></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Facturation"><select className="field" name="billing_model" defaultValue="MONTH">
              <option value="MONTH">Mensuel</option><option value="YEAR">Annuel</option>
              <option value="ONE_TIME">Paiement unique</option><option value="PER_USE">À l'utilisation</option>
              <option value="QUOTE">Sur devis</option>
            </select></Field>
            <Field label="Montant FCFA (laisser vide sur devis)"><input className="field" name="price_xof" type="number" min={0} placeholder="0"/></Field>
          </div>
          <Field label="Description"><textarea className="field" name="description" rows={3}/></Field>
        </>}
        {form === "campaign" && <>
          <Field label="Nom de la campagne"><input className="field" name="title" required maxLength={160}/></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Canal"><select className="field" name="channel"><option value="website">Site web</option><option value="social">Réseaux sociaux</option><option value="partners">Partenaires</option><option value="field">Terrain</option></select></Field>
            <Field label="Budget FCFA"><input className="field" name="budget_xof" type="number" min={0} defaultValue={0} required/></Field>
          </div>
          <p className="text-xs text-slate-500">La campagne est créée en brouillon. Le responsable commercial pourra l'activer.</p>
        </>}
        {form === "member" && <>
          <Field label="Email d'un compte SahelTech existant"><input className="field" name="email" type="email" required/></Field>
          <Field label="Rôle"><select className="field" name="role">
            {(["MANAGER", "MARKETING", "SALES", "FINANCE", "SUPPORT"] as ServiceRole[]).map(item => <option value={item} key={item}>{roles[item]}</option>)}
          </select></Field>
          <p className="text-xs text-slate-500">Un seul compte SahelTech, avec des autorisations séparées par service.</p>
        </>}
        <ErrorMessage value={error}/>
        <div className="flex justify-end gap-2 border-t pt-4">
          <button type="button" className="btn-secondary" onClick={() => setForm(null)}>Annuler</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </form>
    </Modal>}
  </div>;
}
